import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import CustomWallpaperUpsellCallouts from '@wetransfer/transfer/constant/flags/custom-wallpaper-upsell-callouts';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { PRICING } from '@wetransfer/transfer/constant/routes';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import browser from '@wetransfer/transfer/modules/concorde-browser/browser';
import concordeCookie from '@wetransfer/transfer/modules/concorde-cookie';
import { useExperiments } from '@wetransfer/transfer/modules/experiments/hooks';
import Router from '@wetransfer/transfer/modules/router';
import { selectIsSessionTandC } from '@wetransfer/transfer/store/selector/session';
import { selectTransferState } from '@wetransfer/transfer/store/selector/transfer';
import { selectUserPlanTier } from '@wetransfer/transfer/store/selector/user';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';

import { POPOVER_DISMISSED_FOREVER_COOKIE_KEY } from './constants';
import {
  createFeaturePromptEvent,
  setWasPopoverDismissedCookie,
} from './utils';

export const useOptionsCustomWallpaperUpsellPopover = (): {
  isVisible: boolean;
  onDismiss: () => void;
  onAccept: () => void;
} => {
  const [isVisible, setIsVisible] = useState(false);
  const isAmplitudeReady = useExperiments().isReady;
  const transferState = useSelector(selectTransferState);
  const userPlanTier = useSelector(selectUserPlanTier);
  const isTandCAccepted = useSelector(selectIsSessionTandC);

  const wasPopoverDismissedForever =
    concordeCookie.get(POPOVER_DISMISSED_FOREVER_COOKIE_KEY) === 'true';

  useEffect(() => {
    if (
      isTandCAccepted &&
      !browser.isMobile &&
      !wasPopoverDismissedForever &&
      isAmplitudeReady &&
      CustomWallpaperUpsellCallouts.isOn() &&
      transferState === TRANSFER_STATES.INIT &&
      userPlanTier !== PLAN_TIER.PREMIUM_2022
    ) {
      setIsVisible(true);
      trackAmplitudeEvent(createFeaturePromptEvent('Shown', 'Popover'));
    }
  }, [
    isTandCAccepted,
    isAmplitudeReady,
    transferState,
    userPlanTier,
    wasPopoverDismissedForever,
  ]);

  const onDismiss = useCallback(() => {
    setIsVisible(false);
    setWasPopoverDismissedCookie();
    trackAmplitudeEvent(createFeaturePromptEvent('Dismiss', 'Popover'));
  }, []);

  const onAccept = useCallback(() => {
    Router.navigateTo(PRICING.route);
    setIsVisible(false);
    setWasPopoverDismissedCookie();
    trackAmplitudeEvent(createFeaturePromptEvent('Confirm', 'Popover'));
  }, []);

  return {
    onDismiss,
    onAccept,
    isVisible,
  };
};
